import { useState, useEffect, useRef } from "react";

const getAlgebraicNotation = (row, col) => {
  const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
  const rank = 8 - row;
  return `${files[col]}${rank}`;
};

const THEMES = {
  green: {
    light: '#B0CE88',
    dark: '#4C763B',
    highlightCheck: '#E05858', 
    highlightMoveFrom: '#9BE59B',
    highlightMoveTo: '#6FD36F',
    highlightPossible: '#FFFD8F',
    highlightCapture: '#E05858',
    borderColor: '#043915'
  },
  highContrast: {
    light: '#FFFFFF',
    dark: '#000000',
    highlightCheck: '#FF0000', 
    highlightMoveFrom: '#FFFF00',
    highlightMoveTo: '#FFD700',  
    highlightPossible: '#00FFFF', 
    highlightCapture: '#FF4500',  
    borderColor: '#FFFFFF' 
  },
  blue: {
    light: '#DEE3E6',
    dark: '#8CA2AD',
    highlightCheck: '#FF6F61',
    highlightMoveFrom: '#AEC6CF',
    highlightMoveTo: '#779ECB',
    highlightPossible: '#FDFD96',
    highlightCapture: '#FF6961',
    borderColor: '#2C3E50'
  }
};

function ChessBoard({
  board,
  selectedPiece,
  onSquareClick,
  settings,
  validMoves = [],
  checkedSquare = null,
  lastMove = null,
  promotionState,
  onPromotionSelect,
  myColor
}) {
  const boardRef = useRef(null);

  const [responsiveMetrics, setResponsiveMetrics] = useState({
    square: 40,
    font: 30
  });

  const activeThemeKey = settings?.theme && THEMES[settings.theme] ? settings.theme : 'green';
  const colors = THEMES[activeThemeKey];

  useEffect(() => {
    if (!settings || settings.layoutMode !== 'responsive') return;

    const calculateSize = () => {
      if (boardRef.current) {
        const availableWidth = boardRef.current.clientWidth;
        const availableHeight = boardRef.current.clientHeight;

        if (availableWidth === 0 || availableHeight === 0) return;

        const minDimension = Math.min(availableWidth, availableHeight) - 20;
        const newSquareSize = Math.floor(minDimension / 8);

        if (newSquareSize > 10) {
          setResponsiveMetrics({
            square: newSquareSize,
            font: Math.floor(newSquareSize * 0.70)
          });
        }
      }
    };

    calculateSize();
    const timer = setTimeout(calculateSize, 100);
    const resizeObserver = new ResizeObserver(() => {
      calculateSize();
    });

    if (boardRef.current) {
      resizeObserver.observe(boardRef.current);
    }

    window.addEventListener('resize', calculateSize);

    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
      window.removeEventListener('resize', calculateSize);
    };
  }, [settings?.layoutMode]);

  const currentSettings = settings || { squareSize: 60, pieceFontSize: 45, layoutMode: 'responsive' };
  const isResponsive = currentSettings.layoutMode === 'responsive';

  const effectiveSquareSize = isResponsive ? responsiveMetrics.square : currentSettings.squareSize;
  const effectiveFontSize = isResponsive ? responsiveMetrics.font : currentSettings.pieceFontSize;

  const getPieceSymbol = (piece) => {
    const symbols = {
      king: '♔', queen: '♕', rook: '♖',
      bishop: '♗', knight: '♘', pawn: '♙'
    };
    return piece.color === 'black'
      ? symbols[piece.type]?.toLowerCase?.()
      : symbols[piece.type];
  };

  const getPromotionPieceSymbol = (pieceType, color) => {
    const symbols = {
      q: '♕',
      r: '♖',
      b: '♗',
      n: '♘'
    };
    return color === 'black' ? symbols[pieceType]?.toLowerCase?.() : symbols[pieceType];
  };

  const getPromotionPanelPosition = (row, col) => {
    if (col >= 6) {
      return { left: 'auto', right: '100%', transform: 'translateY(-50%)' };
    }
    return { left: '100%', right: 'auto', transform: 'translateY(-50%)' };
  };


  return (
    <div className="w-100 h-100 d-flex justify-content-center align-items-center" ref={boardRef}>
      <div style={{
        position: 'relative',
        border: `4px solid ${colors.borderColor}`,

        borderRadius: '5px',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        transition: 'all 0.2s ease-out'
      }}>
        {board.map((row, rowIndex) => (
          <div key={rowIndex} style={{ display: 'flex' }}>
            {row.map((piece, colIndex) => {

              const currentSquareId = getAlgebraicNotation(rowIndex, colIndex);
              const isPossibleMove = validMoves.includes(currentSquareId);
              const isCheck = checkedSquare === currentSquareId;

              const isLastFrom = lastMove?.from === currentSquareId;
              const isLastTo = lastMove?.to === currentSquareId;

              const isSelected = selectedPiece &&
                selectedPiece.row === rowIndex &&
                selectedPiece.col === colIndex;
              let backgroundColor = (rowIndex + colIndex) % 2 === 0 ? colors.light : colors.dark;

              if (isLastFrom) backgroundColor = colors.highlightMoveFrom;
              if (isLastTo) backgroundColor = colors.highlightMoveTo;

              if (isCheck) {
                backgroundColor = colors.highlightCheck;
              }
              else if (isPossibleMove) {
                if (piece) {
                  backgroundColor = colors.highlightCapture;
                } else {
                  backgroundColor = colors.highlightPossible;
                }
              }
              const isPromotionSquare = promotionState &&
                promotionState.row === rowIndex &&
                promotionState.col === colIndex;

              const selectionColor = activeThemeKey === 'highContrast' ? '#00FFFF' : 'blue';

              return (
                <div
                  key={`${rowIndex}-${colIndex}`}
                  style={{
                    width: `${effectiveSquareSize}px`,
                    height: `${effectiveSquareSize}px`,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    border: activeThemeKey === 'highContrast' ? '1px solid #555' : `1px solid ${colors.borderColor}`,
                    backgroundColor: backgroundColor,
                    cursor: piece ? 'pointer' : 'default',
                    boxShadow: isSelected ? `inset 0 0 0 3px ${selectionColor}` : 'none',
                    position: 'relative'
                  }}
                  onClick={() => onSquareClick(rowIndex, colIndex)}
                >
                  {isPossibleMove && !piece && (
                    <div style={{
                      position: 'absolute',
                      width: '20%',
                      height: '20%',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(0,0,0,0.5)' 
                    }}></div>
                  )}

                  {piece && (
                    <span style={{
                      fontSize: `${effectiveFontSize}px`,
                      color: piece.color === 'white' ? '#FFF' : '#000',
                      textShadow: piece.color === 'white' 
                        ? '1px 1px 2px #000, 0px 0px 2px #000' 
                        : '1px 1px 2px #FFF, 0px 0px 2px #FFF',
                      userSelect: 'none',
                      lineHeight: 1,
                      zIndex: 2
                    }}>
                      {getPieceSymbol(piece)}
                    </span>
                  )}

                  {isPromotionSquare && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        ...getPromotionPanelPosition(rowIndex, colIndex),
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '2px',
                        marginLeft: '8px',
                        backgroundColor: 'black',
                        border: '3.5px solid #ffff',
                        borderRadius: '4px',
                        padding: '4px',
                        zIndex: 1000,
                        boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {['q', 'r', 'b', 'n'].map((pieceType) => (
                        <button
                          key={pieceType}
                          onClick={() => onPromotionSelect(pieceType)}
                          style={{
                            width: `${effectiveSquareSize * 0.5}px`,
                            height: `${effectiveSquareSize * 0.5}px`,
                            backgroundColor: colors.dark, 
                            border: `1px solid ${colors.light}`,
                            borderRadius: '3px',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'center',
                            fontSize: `${effectiveFontSize * 0.7}px`,
                            color: myColor === 'w' ? '#FFF' : '#000',
                            textShadow: myColor === 'w' ? '1px 1px 2px #000' : '1px 1px 2px #FFF',
                            transition: 'background-color 0.2s'
                          }}
                        >
                          {getPromotionPieceSymbol(pieceType, myColor === 'w' ? 'white' : 'black')}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
export default ChessBoard;